/* CSV import — handover §12, and §14 step 5.
 *
 * "Ship this at launch. Nobody types 40 assets in by hand, and a system that
 * starts empty stays empty."
 *
 * Four steps: drop the file, confirm the mapping, read the dry run, apply. The
 * dry run is not a formality — it names every row it will create, every field it
 * will change with old and new values, and every row it will skip and why. You
 * should be able to spot a wrongly-mapped column before it writes, not after.
 *
 * All the logic lives in lib/csv.ts and lib/importPlan.ts, both covered by tests.
 * This file is the wizard around them.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOnline } from '../../hooks/useOnline';
import { parseCsv, type CsvTable } from '../../lib/csv';
import {
  autoMap,
  buildPlan,
  IMPORT_TARGETS,
  type ExistingRecord,
  type ImportPlan,
  type ImportTarget,
  type PlannedRow,
} from '../../lib/importPlan';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import type { IDevice, IPerson } from '../../models';
import { Badge, Button, DataTable, EmptyState, Panel, Select, type Column } from '../ui';
import s from './ImportScreen.module.scss';
import screen from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

type Stage = 'file' | 'map' | 'review' | 'done';

const STAGES: { id: Stage; label: string }[] = [
  { id: 'file', label: 'Choose a file' },
  { id: 'map', label: 'Map columns' },
  { id: 'review', label: 'Dry run' },
  { id: 'done', label: 'Applied' },
];

/* One counter on the dry run and on the result. Same card shape as a dashboard
   tile, so the tone carries its usual meaning: green is a write that will land,
   amber wants reading, red did not happen. */
function Sum({
  tone,
  icon,
  value,
  label,
}: {
  tone: 'create' | 'update' | 'skip' | 'warn' | 'bad';
  icon: string;
  value: number;
  label: string;
}) {
  const tones = {
    create: s.sumCreate,
    update: s.sumUpdate,
    skip: s.sumSkip,
    warn: s.sumWarn,
    bad: s.sumBad,
  };
  return (
    <div className={`${s.sumCard} ${tones[tone]}`}>
      <span className={s.sumIcon}>
        <i className={`ti ${icon}`} aria-hidden />
      </span>
      <div>
        <b className={s.sumNum}>{value}</b>
        <span className={s.sumLabel}>{label}</span>
      </div>
    </div>
  );
}

export function ImportScreen() {
  const svc = useServices();
  const online = useOnline();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<Stage>('file');
  const [targetId, setTargetId] = useState<ImportTarget['id']>('devices');
  const [fileName, setFileName] = useState('');
  const [table, setTable] = useState<CsvTable | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [dragging, setDragging] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [applied, setApplied] = useState<{ created: number; updated: number; failed: string[] } | null>(null);

  const target = IMPORT_TARGETS.find((t) => t.id === targetId)!;

  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });

  /* Existing records, shaped for the diff. The key is what §12 matches on: asset
     tag for devices, UPN for people. */
  const existing = useMemo<ExistingRecord[]>(() => {
    if (targetId === 'devices') {
      return (devices.data ?? []).map((d) => ({
        id: d.id,
        key: d.tag,
        values: d as unknown as Record<string, unknown>,
      }));
    }
    return (people.data ?? []).map((p) => ({
      id: p.id,
      key: p.upn,
      values: p as unknown as Record<string, unknown>,
    }));
  }, [targetId, devices.data, people.data]);

  const plan: ImportPlan | null = useMemo(
    () => (table ? buildPlan(table, mapping, target, existing) : null),
    [table, mapping, target, existing],
  );

  const readFile = useCallback(
    async (file: File) => {
      setReadError(null);
      try {
        const text = await file.text();
        const parsed = parseCsv(text);
        if (parsed.headers.length === 0) {
          setReadError('That file has no header row.');
          return;
        }
        if (parsed.rows.length === 0) {
          setReadError('That file has a header but no data rows.');
          return;
        }
        setFileName(file.name);
        setTable(parsed);
        setMapping(autoMap(parsed.headers, target));
        setStage('map');
      } catch (err) {
        setReadError(err instanceof Error ? err.message : 'Could not read that file.');
      }
    },
    [target],
  );

  /* Writes go one row at a time rather than in a batch. Slower, but a failure on
     row 30 leaves rows 1–29 written and reported, instead of an all-or-nothing
     outcome that tells you nothing about what landed. */
  const apply = useMutation({
    meta: { quiet: true },
    mutationFn: async () => {
      if (!plan) throw new Error('No plan');
      let created = 0;
      let updated = 0;
      const failed: string[] = [];

      for (const row of plan.rows) {
        if (row.action === 'skip') continue;
        try {
          if (targetId === 'devices') {
            if (row.action === 'create') {
              await svc.devices.create({
                tag: '', type: 'Laptop', make: '', model: '', serial: '', cpu: '', ram: '',
                disk: '', os: '', bought: '', warranty: '', user: null, loc: 'Hull', ip: '',
                status: 'In use', compliance: 'Unknown', seen: '', enc: '', src: 'Imported',
                notes: '',
                ...(row.values as Partial<IDevice>),
              } as Omit<IDevice, 'id'>);
              created += 1;
            } else {
              await svc.devices.update(row.existingId!, row.values as Partial<IDevice>);
              updated += 1;
            }
          } else if (row.action === 'create') {
            await svc.people.create({
              name: '', upn: '', title: '', dept: '', loc: 'Hull', status: 'Active',
              mobile: '', start: '', mgr: '', lic: [], groups: [], mfa: '', notes: '',
              ...(row.values as Partial<IPerson>),
            } as Omit<IPerson, 'id'>);
            created += 1;
          } else {
            /* People are sync-owned apart from Notes (§10 rule 1), so an import
               can only write that. Anything else would be reverted at 02:00. */
            const notes = row.values.notes;
            if (typeof notes === 'string') {
              await svc.people.saveNotes(row.existingId!, notes);
              updated += 1;
            }
          }
        } catch (err) {
          failed.push(`${row.keyValue}: ${err instanceof Error ? err.message : 'failed'}`);
        }
      }
      return { created, updated, failed };
    },
    onSuccess: (result) => {
      setApplied(result);
      setStage('done');
      void qc.invalidateQueries();
    },
  });

  const reset = () => {
    setStage('file');
    setTable(null);
    setMapping({});
    setFileName('');
    setApplied(null);
    setReadError(null);
    apply.reset();
  };

  /* People updates can only touch Notes. Say so up front rather than silently
     dropping the other columns in the apply step. */
  const peopleUpdateLimited =
    targetId === 'people' && (plan?.counts.update ?? 0) > 0 && !('notes' in (mapping ? Object.values(mapping).reduce((acc, v) => ({ ...acc, [v]: true }), {} as Record<string, boolean>) : {}));

  const rowColumns: Column<PlannedRow>[] = [
    { key: 'line', header: 'Line', width: '64px', sortBy: (r) => r.line, render: (r) => <span className={screen.cellSub}>{r.line}</span> },
    {
      key: 'action',
      header: 'Action',
      width: '110px',
      sortBy: (r) => r.action,
      render: (r) => (
        <Badge tone={r.action === 'create' ? 'ok' : r.action === 'update' ? 'info' : 'muted'}>
          {r.action}
        </Badge>
      ),
    },
    {
      key: 'key',
      header: target.fields.find((f) => f.isKey)?.label ?? 'Key',
      sortBy: (r) => r.keyValue,
      render: (r) => <span className={screen.mono}>{r.keyValue || '—'}</span>,
    },
    {
      key: 'detail',
      header: 'What happens',
      render: (r) => {
        if (r.action === 'skip') return <span className={screen.cellSub}>{r.reason}</span>;
        if (r.action === 'create') {
          return (
            <span className={screen.cellSub}>
              new record, {Object.keys(r.values).length} field
              {Object.keys(r.values).length === 1 ? '' : 's'} set
            </span>
          );
        }
        return (
          <div className={s.changeList}>
            {r.changes.map((c) => (
              <div key={c.field} className={s.change}>
                <b>{c.label}</b>{' '}
                <span className={s.from}>{String(c.from ?? '—') || '—'}</span>{' '}
                <span aria-hidden>→</span> <span className={s.to}>{String(c.to)}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: 'warnings',
      header: '',
      render: (r) =>
        r.warnings.length > 0 ? (
          <div className={s.warnList} title={r.warnings.join('\n')}>
            <i className="ti ti-alert-triangle" aria-hidden /> {r.warnings.length}
          </div>
        ) : null,
    },
  ];

  const stageIndex = STAGES.findIndex((x) => x.id === stage);

  return (
    <>
      <div className={screen.head}>
        <div className={screen.grow}>
          <h1>Import</h1>
          <p>
            Drop in a CSV, confirm the columns, read the dry run, then apply. Re-importing a
            corrected file updates rather than duplicates; unmapped columns are left untouched.
          </p>
        </div>
      </div>

      <div className={s.steps}>
        {STAGES.map((x, i) => (
          <div
            key={x.id}
            className={`${s.step} ${x.id === stage ? s.stepOn : ''} ${i < stageIndex ? s.stepDone : ''}`}
          >
            <span className={s.stepNum}>{i < stageIndex ? '✓' : i + 1}</span>
            {x.label}
          </div>
        ))}
      </div>

      <div className={screen.stack}>
        {/* ── Step 1 ── */}
        {stage === 'file' && (
          <Panel title="Choose a file" sub="A header row, then one row per record">
            <div className={s.inner}>
              <div className={s.rowGap}>
                <span className={screen.cellSub}>Importing into</span>
                <Select
                  label="Import target"
                  value={targetId}
                  onChange={(v) => {
                    setTargetId(v as ImportTarget['id']);
                    setTable(null);
                  }}
                  options={IMPORT_TARGETS.map((t) => ({ value: t.id, label: t.label }))}
                />
              </div>

              <div
                className={`${s.drop} ${dragging ? s.dropActive : ''}`}
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  const f = e.dataTransfer.files[0];
                  if (f) void readFile(f);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click();
                }}
              >
                <i className="ti ti-file-spreadsheet" aria-hidden />
                <div className={s.dropTitle}>Drop a CSV here, or click to choose one</div>
                <div className={s.dropHint}>
                  Excel exports work as they come — quoted fields, embedded commas and newlines,
                  semicolon delimiters and a UTF-8 byte-order mark are all handled.
                </div>
              </div>

              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv,text/plain"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void readFile(f);
                }}
              />

              {readError && <div className={s.errorBox}>{readError}</div>}
            </div>
          </Panel>
        )}

        {/* ── Step 2 ── */}
        {stage === 'map' && table && (
          <Panel
            title="Map columns"
            sub={`${table.headers.length} columns found — matched by name where possible`}
            actions={
              <Button icon="ti-x" onClick={reset}>
                Start again
              </Button>
            }
          >
            <div className={s.inner}>
              <div className={s.fileRow}>
                <i className="ti ti-file-check" aria-hidden />
                <div style={{ flex: 1 }}>
                  <b>{fileName}</b>
                  <div className={screen.cellSub}>
                    {table.rows.length} data row{table.rows.length === 1 ? '' : 's'}
                    {table.malformed.length > 0 &&
                      ` · ${table.malformed.length} row(s) had an unexpected column count`}
                  </div>
                </div>
              </div>

              <div className={s.mapGrid}>
                <div className={s.mapHeader}>Column in your file</div>
                <div />
                <div className={s.mapHeader}>{PRODUCT.name} field</div>

                {table.headers.map((header) => {
                  const chosen = mapping[header] ?? '';
                  const field = target.fields.find((f) => f.key === chosen);
                  const sample = table.rows.find((r) => r[header])?.[header] ?? '';
                  return (
                    <div key={header} style={{ display: 'contents' }}>
                      <div className={s.mapSource}>
                        {header}
                        {sample && <span className={s.mapSample}>e.g. {sample}</span>}
                      </div>
                      <div className={s.mapArrow} aria-hidden>
                        →
                      </div>
                      <select
                        className={`${s.mapSelect} ${!chosen ? s.mapSelectUnset : ''} ${field?.isKey ? s.mapSelectKey : ''}`}
                        value={chosen}
                        aria-label={`Map ${header}`}
                        onChange={(e) => {
                          const next = { ...mapping };
                          if (!e.target.value) delete next[header];
                          else {
                            // One source column per field — clear any other header
                            // already claiming it.
                            for (const [h, k] of Object.entries(next)) {
                              if (k === e.target.value && h !== header) delete next[h];
                            }
                            next[header] = e.target.value;
                          }
                          setMapping(next);
                        }}
                      >
                        <option value="">Ignore this column</option>
                        {target.fields.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                            {f.isKey ? ' — match key' : ''}
                            {f.required && !f.isKey ? ' (required)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              {plan && plan.missingRequired.length > 0 && (
                <div className={s.errorBox}>
                  Map a column to{' '}
                  {plan.missingRequired.map((f) => f.label).join(' and ')} before continuing.
                  {plan.missingRequired.some((f) => f.isKey) &&
                    ' The match key is what tells an update apart from a new record.'}
                </div>
              )}

              <div className={s.actions}>
                <Button
                  primary
                  icon="ti-arrow-right"
                  disabled={!plan || plan.missingRequired.length > 0}
                  onClick={() => setStage('review')}
                >
                  Dry run
                </Button>
              </div>
            </div>
          </Panel>
        )}

        {/* ── Step 3 ── */}
        {stage === 'review' && plan && (
          <>
            <Panel title="Dry run" sub="Nothing has been written yet">
              <div className={s.inner}>
                <div className={s.summary}>
                  <Sum tone="create" icon="ti-circle-plus" value={plan.counts.create} label="to create" />
                  <Sum tone="update" icon="ti-pencil" value={plan.counts.update} label="to update" />
                  <Sum tone="skip" icon="ti-equal" value={plan.counts.unchanged} label="already match" />
                  <Sum tone="skip" icon="ti-arrow-bounce" value={plan.counts.skip} label="skipped" />
                  <Sum
                    tone="warn"
                    icon="ti-alert-triangle"
                    value={plan.rows.reduce((n, r) => n + r.warnings.length, 0)}
                    label="warnings"
                  />
                </div>

                {plan.unmappedHeaders.length > 0 && (
                  <div className={s.note}>
                    <i className="ti ti-info-circle" aria-hidden />
                    <span>
                      Ignoring {plan.unmappedHeaders.length} column
                      {plan.unmappedHeaders.length === 1 ? '' : 's'}:{' '}
                      {plan.unmappedHeaders.join(', ')}. Nothing in those columns will be written,
                      and existing values in unmapped fields are left as they are.
                    </span>
                  </div>
                )}

                {peopleUpdateLimited && (
                  <div className={s.note}>
                    <i className="ti ti-lock" aria-hidden />
                    <span>
                      People records are written by the nightly Entra sync, so an import can only
                      set <b>Notes</b> on someone who already exists. The {plan.counts.update}{' '}
                      update{plan.counts.update === 1 ? '' : 's'} below will be skipped unless you
                      map a Notes column. New people are created in full.
                    </span>
                  </div>
                )}

                {!online && (
                  <div className={s.errorBox}>
                    You are offline. Imports write to SharePoint, so this needs a network.
                  </div>
                )}

                <div className={s.actions}>
                  <Button
                    primary
                    icon="ti-database-import"
                    disabled={!plan.ready || !online || apply.isPending}
                    onClick={() => apply.mutate()}
                  >
                    {apply.isPending
                      ? 'Applying…'
                      : `Apply — ${plan.counts.create} create, ${plan.counts.update} update`}
                  </Button>
                  <Button icon="ti-arrow-left" onClick={() => setStage('map')}>
                    Back to mapping
                  </Button>
                  <Button icon="ti-x" onClick={reset}>
                    Start again
                  </Button>
                </div>

                {!plan.ready && plan.counts.create + plan.counts.update === 0 && (
                  <div className={s.note}>
                    <i className="ti ti-checks" aria-hidden />
                    <span>
                      Nothing to do — every row in this file already matches what is in the
                      register. That is what you want to see on a re-import.
                    </span>
                  </div>
                )}

                {apply.isError && (
                  <div className={s.errorBox}>
                    {apply.error instanceof Error ? apply.error.message : 'The import failed.'}
                  </div>
                )}
              </div>
            </Panel>

            <Panel title="Row by row" sub="Every change this import will make" flush>
              <DataTable
                rows={plan.rows}
                columns={rowColumns}
                rowKey={(r) => String(r.line)}
                initialSort={{ key: 'line', dir: 'asc' }}
                empty={<EmptyState icon="ti-inbox" title="No rows" />}
              />
            </Panel>
          </>
        )}

        {/* ── Step 4 ── */}
        {stage === 'done' && applied && (
          <Panel title="Import applied">
            <div className={s.inner}>
              <div className={s.summary}>
                <Sum tone="create" icon="ti-circle-plus" value={applied.created} label="created" />
                <Sum tone="update" icon="ti-pencil" value={applied.updated} label="updated" />
                {applied.failed.length > 0 && (
                  <Sum
                    tone="bad"
                    icon="ti-alert-octagon"
                    value={applied.failed.length}
                    label="failed"
                  />
                )}
              </div>

              {applied.failed.length > 0 && (
                <div className={s.errorBox}>
                  <b>These rows did not write:</b>
                  <ul className={s.bullets}>
                    {applied.failed.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  Everything else was written. Fix these rows and re-import the same file — the
                  rows that already landed will show as already matching.
                </div>
              )}

              <div className={s.note}>
                <i className="ti ti-history" aria-hidden />
                <span>Every create and update above is in the audit trail.</span>
              </div>

              <div className={s.actions}>
                <Button primary icon="ti-upload" onClick={reset}>
                  Import another file
                </Button>
              </div>
            </div>
          </Panel>
        )}
      </div>
    </>
  );
}
